import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { IPartnerFinSobRow } from '../../model'

import { getPlanRows } from './getPlanRows'
import { getRevenueMeasureField } from './getRevenueMeasureField'
import { getWeightedSob, TFinSobData } from './getWeightedSob'
import { prorateCurrentQuarterRows } from './prorateCurrentQuarterRows'
import { sumMeasure } from './sumMeasure'
import { IRevenueAndSobFigures, TCategoryRowScope } from './types'

const EMPTY_PLAN: IRevenueAndSobFigures = {
  sales: null,
  sob: null,
}

interface IParams {
  rows: IPartnerFinSobRow[]
  /** The financial year the tile is showing. Unlike the baseline, the plan reads this same year,
   *  as-is. */
  selectedFYear: number
  actualsType: ActualsValueTypes
  isLocalCurrency: boolean
  currentFQuarter: number
  /** `null` outside YTD scope. Otherwise, the quarter matching `currentFQuarter` is prorated by
   *  this percentage instead of summed in full. */
  fQuarterActualsPercentage: number | null
  /** Defaults to `OVERALL_CATEGORY` (the offering total) when omitted. */
  category?: string
  categoryRowScope?: TCategoryRowScope
}

/** The tile's plan column: what the partner is targeted to do this year.
 *
 *  Revenue is additive, so it is a plain sum. SOB is a share, so it only rolls up as an average
 *  weighted by the same revenue measure — a big offering's share must count for more than a small
 *  one's. */
export const getRevenueAndSobPlan = ({
  rows,
  selectedFYear,
  actualsType,
  isLocalCurrency,
  currentFQuarter,
  fQuarterActualsPercentage,
  category,
  categoryRowScope,
}: IParams): IRevenueAndSobFigures => {
  const isYtd = fQuarterActualsPercentage !== null
  const measureField = getRevenueMeasureField(actualsType, isLocalCurrency)

  if (measureField === null) {
    return EMPTY_PLAN
  }

  const planRows = getPlanRows({
    rows,
    selectedFYear,
    category,
    categoryRowScope,
  })

  const scopedRows = isYtd
    ? prorateCurrentQuarterRows({
        rows: planRows,
        measureField,
        currentFQuarter,
        fQuarterActualsPercentage,
      })
    : planRows

  const rowData = scopedRows.map<TFinSobData>((row) => ({
    sob: row.sob,
    revenue: row[measureField],
  }))

  const totalSales = sumMeasure(scopedRows, measureField)
  const weightedSob = getWeightedSob(rowData)

  return {
    sales: totalSales,
    sob: weightedSob,
  }
}
